import { useTranslations } from 'next-intl';
import { useState } from 'react';

import { UiProductCounterInput } from '@/ui/product-counter-input';
import { UiKitButtonTab } from '@/ui-kit/button-tab';
import { BeforeMaskedStateChangeStates, UiKitInput } from '@/ui-kit/input';
import { formattedPrice } from '@/ui-kit/price';
import { formattedNumber } from '@/util/formattedNumber';

import { giftCardPrices } from './constants';
import { styles } from './GiftCardForm.styles';

export interface GiftCardFormProps {
  quantity: number;
  onChangeQuantity: (quantity: number) => void;
  nominal: number;
  onChangeNominal: (nominal: number) => void;
  errorMessage?: string;
}

export function GiftCardForm({
  quantity,
  onChangeQuantity,
  nominal,
  onChangeNominal,
  errorMessage
}: GiftCardFormProps) {
  const slots = styles();
  const t = useTranslations();

  const [isOtherNominal, setIsOtherNominal] = useState(false);

  const beforeMaskedStateChange = ({ nextState }: BeforeMaskedStateChangeStates) => {
    const raw = nextState.value.replace(/[^\d]/g, '');

    const formatted = formattedNumber(Number(raw));

    const newValue = formatted && +raw > 0 ? `${formatted} ₽` : '';

    let cursor = nextState.selection?.start ?? newValue.length;

    const currencyIndex = newValue.indexOf('₽');
    if (cursor > currencyIndex) {
      cursor = currencyIndex;
    }

    return {
      ...nextState,
      value: newValue,
      selection: {
        start: cursor,
        end: cursor
      }
    };
  };

  return (
    <div
      className={slots.wrapper()}
      data-field-error-anchor="nominal"
      tabIndex={-1}
    >
      <div className={slots.base()}>
        <div className={slots.main()}>
          <div className={slots.chips()}>
            {giftCardPrices.map((price) => (
              <UiKitButtonTab
                key={price}
                active={!isOtherNominal && nominal === price}
                onClick={() => onChangeNominal(price)}
              >
                {formattedPrice(price)}
              </UiKitButtonTab>
            ))}
            <UiKitButtonTab
              key="other"
              active={isOtherNominal}
              onClick={() => setIsOtherNominal(true)}
            >
              {t('giftCard.form.otherSum')}
            </UiKitButtonTab>
          </div>
          {isOtherNominal && (
            <div className={slots.price()}>
              <UiKitInput
                label={t('giftCard.form.otherSumLabel')}
                mask="999 999 999 ₽"
                beforeMaskedStateChange={beforeMaskedStateChange}
                data-field-error-focus="nominal"
                onBlur={(event) =>
                  onChangeNominal(+event.target.value.replace(/[^\d]/g, ''))
                }
              />
            </div>
          )}
        </div>
        <div className={slots.quantity()}>
          <div className={slots.quantityTitle()}>{t('count')}</div>
          <UiProductCounterInput
            value={quantity}
            onChange={onChangeQuantity}
            hideDeleteIcon
            min={1}
          />
        </div>
      </div>
      {errorMessage && (
        <p
          className={slots.errorMessage()}
          role="alert"
        >
          {errorMessage}
        </p>
      )}
    </div>
  );
}
